import { useState, useEffect } from 'react';
import { getISTDateString } from '../lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getDailyHisab, recordDailyHisab, updateDailyHisab, deleteDailyHisab, getGuests, getExpenses, getOpeningBalance } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { toast } from 'sonner';
import { CalendarDays, Plus, Wallet, CreditCard, TrendingUp, Pencil, Trash2, Save, X, TrendingDown, Receipt, Calendar, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const guestTypes = [
  { value: 'occupied', label: 'Occupied Guest' },
  { value: 'new_join', label: 'New Join' },
  { value: 'leave', label: 'Leave Guest' },
  { value: 'shifting', label: 'Shifting Guest' },
  { value: 'notice', label: 'Notice Guest' }
];

export const DailyHisab = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [hisabData, setHisabData] = useState({ entries: [], totals: {}, by_type: {}, daily_chart_data: [] });
  const [lifetimeData, setLifetimeData] = useState({ entries: [], totals: {}, by_type: {}, daily_chart_data: [] });
  const [expensesData, setExpensesData] = useState([]);
  const [opening, setOpening] = useState({ opening_cash: 0, opening_bank: 0, opening_total: 0 });
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getISTDateString());
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [viewMode, setViewMode] = useState('daily');
  const [editingEntry, setEditingEntry] = useState(null);
  
  const [form, setForm] = useState({
    date: selectedDate,
    guest_id: '',
    guest_name: '',
    guest_type: 'occupied',
    room_info: '',
    cash_rent: 0,
    cash_deposit: 0,
    cash_electricity: 0,
    bank_rent: 0,
    bank_deposit: 0,
    bank_electricity: 0,
    remarks: ''
  });

  const [editForm, setEditForm] = useState({
    date: '',
    guest_type: 'occupied',
    cash_rent: 0,
    cash_deposit: 0,
    cash_electricity: 0,
    bank_rent: 0,
    bank_deposit: 0,
    bank_electricity: 0,
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedDate, selectedMonth, viewMode]);

  const fetchData = async () => {
    try {
      const params = viewMode === 'daily' ? { date: selectedDate } : 
                     viewMode === 'monthly' ? { month: selectedMonth } : 
                     { lifetime: true };
      
      const [hisabRes, guestsRes] = await Promise.all([
        getDailyHisab(params),
        getGuests({ status: 'joined' })
      ]);
      
      if (viewMode === 'lifetime') {
        setLifetimeData(hisabRes.data);
      } else {
        setHisabData(hisabRes.data);
      }

      // Opening balance for the cashbook (carries forward from previous period)
      try {
        const openRes = await getOpeningBalance(params);
        setOpening(openRes.data);
      } catch (e) { setOpening({ opening_cash: 0, opening_bank: 0, opening_total: 0 }); }
      
      // Always fetch expenses for all views (Daily, Monthly, Lifetime)
      try {
        const expParams = viewMode === 'daily' ? { date: selectedDate } : 
                         viewMode === 'monthly' ? { month: selectedMonth } : {};
        const expRes = await getExpenses(expParams);
        setExpensesData(expRes.data);
      } catch (e) {}
      
      setGuests(guestsRes.data);
    } catch (err) {
      toast.error('Failed to load daily hisab');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestSelect = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    if (guest) {
      setForm({
        ...form,
        guest_id: guestId,
        guest_name: guest.name,
        room_info: guest.room_info
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await recordDailyHisab({ ...form, date: selectedDate });
      toast.success('Entry recorded');
      setDialogOpen(false);
      setForm({
        date: selectedDate,
        guest_id: '',
        guest_name: '',
        guest_type: 'occupied',
        room_info: '',
        cash_rent: 0,
        cash_deposit: 0,
        cash_electricity: 0,
        bank_rent: 0,
        bank_deposit: 0,
        bank_electricity: 0,
        remarks: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record entry');
    }
  };

  const handleEditClick = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      date: entry.date,
      guest_type: entry.guest_type,
      cash_rent: entry.cash_rent || 0,
      cash_deposit: entry.cash_deposit || 0,
      cash_electricity: entry.cash_electricity || 0,
      bank_rent: entry.bank_rent || 0,
      bank_deposit: entry.bank_deposit || 0,
      bank_electricity: entry.bank_electricity || 0,
      remarks: entry.remarks || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateDailyHisab(editingEntry.id, editForm);
      toast.success('Entry updated');
      setEditDialogOpen(false);
      setEditingEntry(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await deleteDailyHisab(entryId);
      toast.success('Entry deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const currentData = viewMode === 'lifetime' ? lifetimeData : hisabData;
  const totals = currentData.totals || {};

  // Calculate expenses totals by payment mode
  const totalExpenses = expensesData.reduce((sum, e) => sum + e.amount, 0);
  const cashExpenses = expensesData.filter(e => e.payment_mode === 'cash').reduce((sum, e) => sum + e.amount, 0);
  const bankExpenses = expensesData.filter(e => e.payment_mode === 'bank').reduce((sum, e) => sum + e.amount, 0);

  // Opening balances (carried forward from previous period)
  const openingCash = opening.opening_cash || 0;
  const openingBank = opening.opening_bank || 0;
  const openingTotal = opening.opening_total || 0;

  // Closing balance = Opening + Receipts − Payments (carries to next period)
  const netCashBalance = openingCash + (totals.total_cash || 0) - cashExpenses;
  const netBankBalance = openingBank + (totals.total_bank || 0) - bankExpenses;
  const netTotalBalance = openingTotal + (totals.grand_total || 0) - totalExpenses;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-hisab-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Daily Hisab</h1>
          <p className="text-slate-500 mt-1">View daily receipts & payments summary (entries auto-sync from receipts & payments)</p>
        </div>
      </div>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="daily-tab">
            <CalendarDays className="h-4 w-4" />
            Daily
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="monthly-tab">
            <Calendar className="h-4 w-4" />
            Monthly
          </TabsTrigger>
          <TabsTrigger value="lifetime" className="flex items-center gap-2" data-testid="lifetime-tab">
            <History className="h-4 w-4" />
            Lifetime
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-4">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-[200px]"
            data-testid="hisab-date-input"
          />
        </TabsContent>

        <TabsContent value="monthly" className="mt-4">
          <Input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-[200px]"
            data-testid="hisab-month-input"
          />
        </TabsContent>

        <TabsContent value="lifetime" className="mt-4">
          <p className="text-sm text-slate-500">Showing all-time data with trends</p>
        </TabsContent>
      </Tabs>

      {/* Summary Cards - Row 0: Opening Balance (carried forward) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-amber-200" style={{ animationDelay: '0ms' }} data-testid="opening-cash-card">
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><Wallet className="h-4 w-4 text-amber-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Opening Cash</span></div>
          <p className="text-2xl font-bold font-mono text-amber-700">₹{openingCash.toLocaleString()}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{viewMode === 'daily' ? 'Closing till previous day' : viewMode === 'monthly' ? 'Closing till last month' : 'System opening balance'}</p>
        </div>

        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-sky-200" style={{ animationDelay: '60ms' }} data-testid="opening-bank-card">
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-cyan-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><CreditCard className="h-4 w-4 text-sky-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Opening Bank</span></div>
          <p className="text-2xl font-bold font-mono text-sky-700">₹{openingBank.toLocaleString()}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{viewMode === 'daily' ? 'Closing till previous day' : viewMode === 'monthly' ? 'Closing till last month' : 'System opening balance'}</p>
        </div>

        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-slate-300" style={{ animationDelay: '120ms' }} data-testid="opening-total-card">
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><History className="h-4 w-4 text-slate-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Opening Total</span></div>
          <p className="text-2xl font-bold font-mono text-slate-800">₹{openingTotal.toLocaleString()}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Carried forward balance</p>
        </div>
      </div>

      {/* Summary Cards - Row 1: Receipts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-emerald-200" style={{ animationDelay: '0ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><Wallet className="h-4 w-4 text-emerald-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Cash Receipt</span></div>
          <p className="text-2xl font-bold font-mono text-emerald-700">₹{(totals.total_cash || 0).toLocaleString()}</p>
        </div>

        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-blue-200" style={{ animationDelay: '60ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><CreditCard className="h-4 w-4 text-blue-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Bank Receipt</span></div>
          <p className="text-2xl font-bold font-mono text-blue-700">₹{(totals.total_bank || 0).toLocaleString()}</p>
        </div>

        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-purple-200" style={{ animationDelay: '120ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="h-4 w-4 text-purple-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Total Receipts</span></div>
          <p className="text-2xl font-bold font-mono text-purple-700">₹{(totals.grand_total || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Summary Cards - Row 2: Payments/Expenses */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-rose-200" style={{ animationDelay: '140ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><Wallet className="h-4 w-4 text-rose-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Cash Payment</span></div>
          <p className="text-2xl font-bold font-mono text-rose-700">₹{cashExpenses.toLocaleString()}</p>
        </div>

        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-rose-200" style={{ animationDelay: '180ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><CreditCard className="h-4 w-4 text-rose-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Bank Payment</span></div>
          <p className="text-2xl font-bold font-mono text-rose-700">₹{bankExpenses.toLocaleString()}</p>
        </div>

        <div className="dash-animate relative overflow-hidden rounded-xl bg-white border border-slate-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ring-1 ring-rose-200" style={{ animationDelay: '220ms' }}>
          <div className="absolute -top-5 -right-5 w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 opacity-10"></div>
          <div className="flex items-center gap-1.5 mb-1"><TrendingDown className="h-4 w-4 text-rose-600" /><span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Total Payments</span></div>
          <p className="text-2xl font-bold font-mono text-rose-700">₹{totalExpenses.toLocaleString()}</p>
        </div>
      </div>

      {/* Closing Balances = Opening + Receipts − Payments (carries to next period) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div className={`dash-animate relative overflow-hidden rounded-xl p-4 shadow-sm ${netCashBalance >= 0 ? 'bg-emerald-900' : 'bg-rose-900'}`} style={{ animationDelay: '260ms' }} data-testid="net-cash-card">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="h-4 w-4 text-white/70" />
            <span className="text-xs uppercase tracking-wide text-white/70 font-medium">Closing Cash Balance</span>
          </div>
          <p className="text-2xl font-bold font-mono text-white">₹{netCashBalance.toLocaleString()}</p>
          <p className="text-xs text-white/60 mt-1 font-mono">
            Open ₹{openingCash.toLocaleString()} + Rec ₹{(totals.total_cash || 0).toLocaleString()} − Pay ₹{cashExpenses.toLocaleString()}
          </p>
        </div>

        <div className={`dash-animate relative overflow-hidden rounded-xl p-4 shadow-sm ${netBankBalance >= 0 ? 'bg-blue-900' : 'bg-rose-900'}`} style={{ animationDelay: '300ms' }} data-testid="net-bank-card">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="h-4 w-4 text-white/70" />
            <span className="text-xs uppercase tracking-wide text-white/70 font-medium">Closing Bank Balance</span>
          </div>
          <p className="text-2xl font-bold font-mono text-white">₹{netBankBalance.toLocaleString()}</p>
          <p className="text-xs text-white/60 mt-1 font-mono">
            Open ₹{openingBank.toLocaleString()} + Rec ₹{(totals.total_bank || 0).toLocaleString()} − Pay ₹{bankExpenses.toLocaleString()}
          </p>
        </div>

        <div className={`dash-animate relative overflow-hidden rounded-xl p-4 shadow-sm ${netTotalBalance >= 0 ? 'bg-slate-900' : 'bg-rose-900'}`} style={{ animationDelay: '340ms' }} data-testid="net-total-hero">
          <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white opacity-[0.04]"></div>
          <div className="flex items-center justify-between mb-1 relative">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-white/70" />
              <span className="text-xs uppercase tracking-wide text-white/70 font-medium">Closing Total Balance</span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${netTotalBalance >= 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/30 text-rose-100'}`}>
              {netTotalBalance >= 0 ? '▲ Surplus' : '▼ Deficit'}
            </span>
          </div>
          <p className="text-2xl font-bold font-mono text-white relative">₹{netTotalBalance.toLocaleString()}</p>
          <p className="text-xs text-white/60 mt-1 font-mono relative">
            Open ₹{openingTotal.toLocaleString()} + Rec ₹{(totals.grand_total || 0).toLocaleString()} − Pay ₹{totalExpenses.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Entries Table */}
      <Card className="dash-animate relative overflow-hidden" data-testid="hisab-table" style={{ animationDelay: '380ms' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 z-20" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Receipt Records {viewMode === 'lifetime' && `(${(currentData.entries || []).length} total)`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[500px]">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Guest</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead className="text-right">Cash Rent</TableHead>
                  <TableHead className="text-right">Cash Dep.</TableHead>
                  <TableHead className="text-right">Cash Elec.</TableHead>
                  <TableHead className="text-right">Bank Rent</TableHead>
                  <TableHead className="text-right">Bank Dep.</TableHead>
                  <TableHead className="text-right">Bank Elec.</TableHead>
                  <TableHead className="text-right">Cash Other</TableHead>
                  <TableHead className="text-right">Bank Other</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(currentData.entries || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-14">
                      <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                        <CalendarDays className="w-7 h-7 text-emerald-400" />
                      </div>
                      <p className="font-semibold text-slate-700">No receipts for selected {viewMode === 'daily' ? 'date' : viewMode === 'monthly' ? 'month' : 'period'}</p>
                      <p className="text-sm text-slate-500">Receipts auto-sync here when recorded.</p>
                    </TableCell>
                  </TableRow>
                ) : (currentData.entries || []).map((entry, idx) => {
                  const rowTotal = (entry.cash_rent || 0) + (entry.cash_deposit || 0) + (entry.cash_electricity || 0) + (entry.cash_other || 0) +
                                  (entry.bank_rent || 0) + (entry.bank_deposit || 0) + (entry.bank_electricity || 0) + (entry.bank_other || 0);
                  
                  // Status badge styling (consistent with Guest List, Receipts, Ledger)
                  const getStatusStyle = (type) => {
                    const variants = {
                      booked: 'bg-blue-100 text-blue-700',
                      new_join: 'bg-emerald-100 text-emerald-700',
                      occupied: 'bg-slate-100 text-slate-700',
                      notice: 'bg-amber-100 text-amber-700',
                      shifting: 'bg-purple-100 text-purple-700',
                      left: 'bg-rose-100 text-rose-700',
                      joined: 'bg-emerald-100 text-emerald-700',
                      day_basis: 'bg-purple-100 text-purple-700',
                      overstay: 'bg-red-500 text-white',
                      advance_booking: 'bg-blue-100 text-blue-700',
                      other: 'bg-cyan-100 text-cyan-700'
                    };
                    const labels = {
                      booked: 'Booked',
                      new_join: 'Joined',
                      occupied: 'Occupied',
                      notice: 'Notice',
                      shifting: 'Shifting',
                      left: 'Left',
                      joined: 'Joined',
                      day_basis: 'Day Basis',
                      overstay: 'Overstay',
                      advance_booking: 'Booked',
                      other: 'Other'
                    };
                    return { 
                      style: variants[type] || 'bg-slate-100 text-slate-700', 
                      label: labels[type] || (type ? type.replace('_', ' ') : 'Unknown')
                    };
                  };
                  const statusInfo = getStatusStyle(entry.guest_type);
                  
                  return (
                    <TableRow key={entry.id || idx} data-testid={`hisab-row-${entry.id}`} className="transition-colors hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">{formatDate(entry.date)}</TableCell>
                      <TableCell>{entry.guest_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.style}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60"></span>
                          {statusInfo.label}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{entry.room_info}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{entry.cash_rent || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{entry.cash_deposit || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{entry.cash_electricity || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{entry.bank_rent || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{entry.bank_deposit || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{entry.bank_electricity || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{entry.cash_other || '-'}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{entry.bank_other || '-'}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">₹{rowTotal.toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Records - View Only */}
      <Card className="dash-animate relative overflow-hidden" data-testid="expense-records-table" style={{ animationDelay: '440ms' }}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-pink-600 z-20" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-rose-600" />
            Payment Records {viewMode === 'lifetime' && `(${expensesData.length} total)`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[400px]">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expensesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-14">
                      <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
                        <Receipt className="w-7 h-7 text-rose-400" />
                      </div>
                      <p className="font-semibold text-slate-700">No payments for selected {viewMode === 'daily' ? 'date' : viewMode === 'monthly' ? 'month' : 'period'}</p>
                    </TableCell>
                  </TableRow>
                ) : expensesData.map((expense, idx) => (
                  <TableRow key={expense.id || idx} data-testid={`expense-row-${expense.id}`} className="transition-colors hover:bg-slate-50">
                    <TableCell className="font-mono text-sm">{formatDate(expense.date)}</TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                        {expense.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600">{expense.description}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${expense.payment_mode === 'cash' ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-700'}`}>
                        {expense.payment_mode === 'cash' ? <Wallet className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                        <span className="capitalize">{expense.payment_mode}</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium text-rose-600">
                      ₹{expense.amount.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
